import React, { FC, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import TableTab from "../components/tabs/table-tab";
import { EXPIRY_REASONS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import columns from '../models/ColumnDefinitions/ExpiryReasonColumns';
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from '../models/search/ExpiryReasonSearch';
import SiteSearch from "../models/search/SiteSearch";
import { deleteExpiryReason, getExpiryReasons } from "../services/expiryReasonService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { Category } from "@/models/Category";
import { DeleteEntityResult } from "@/models/services/DeleteEntityResult";
import TableContextStateMonitor from "@/contexts/table/TableContextStateMonitor";
import { removeColumns } from "@/models/utilities/columnHelper";

interface ExpiryReasonsTabProps {
    openModal: (isOpen: boolean) => void;
    setBusy: (isBusy: boolean) => void;
    categories: Category[];
    setCategories: (category: Category[]) => void;
    onFilterChange: (filters: any) => void;
}

const ExpiryReasonsTab: FC<ExpiryReasonsTabProps> = ({ openModal, setBusy, categories, setCategories, onFilterChange }) => {
    const itemPath = "/access-control/configuration/compliance-condition/";
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.operationalCompliance);
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ]

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteExpiryReason(id)
            .then(
                deleteResult => {
                    resolve(deleteResult);
                    setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                    setBusy(false);
                },
                deleteResult => {
                    reject(deleteResult);
                    setBusy(false);
                })
                .finally(() => {
                    setBusy(false);
                });
        });
    }

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const initialiseUserPreferences = (setDetails: (userLoginId, EXPIRY_REASONS) => void) => {
        setDetails(userLoginId, EXPIRY_REASONS);
    }
    // <-- end context

    return <TableContextProvider
        dataMethod={getExpiryReasons}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={removeColumns(columns, ['masterExpiryStartDate', 'masterExpiryEndDate'])}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useFilter
        useSearch
        useSort
        deleteMethod={onDelete}
        lookup={lookup}
        onAddNew={() => openModal(true)}>
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath}
            cyName="operationalCompliance"/>
    </TableContextProvider>;
};

export default ExpiryReasonsTab;
